import i18n from '@/locale'
import { getPrintList, updatePrint } from '@/api/takeaway/order'
import { init } from '@/api/takeaway/setting'
import { TakeawayPrintSetting } from '@/utils/storage'
import { encryptMemberName } from '@/utils'
import { ipc } from '@/utils/ipcRenderer'
import ipcApiRoute from '@/constants/ipcApiRoute'
import { PrintType, BusinessType } from '@/constants'
import Vue from 'vue'

const state = () => ({
  pullInterval: 60 * 1000, //轮询时间
  isPrinting: false, //是否正在打印
  timer: null,
})

const mutations = {
  SET_PRINTING(state, status) {
    state.isPrinting = status
  },
  SET_TIMER(state, timer) {
    state.timer = timer
  },
}

const actions = {
  /**
   * 当前打印列表
   * @param {*} payload
   * @returns
   */
  async FETCH_PRINT_LIST({ state, commit, dispatch }) {
    // 轮询保护
    if (state.isPrinting) return
    commit(`SET_PRINTING`, true)

    // 是否开启自动接单决定是否需要自动打印
    const { MT } = await dispatch('ENABLE_AUTO_RECEIVE')

    // 是否开启了外卖语音播报
    const ifTakeVoice = TakeawayPrintSetting.get()?.ifTakeVoice

    // 发起 http 请求
    try {
      const orderCreateTimeEnd = dayjs().valueOf()
      const { data } = await getPrintList({
        isPrint: false,
        orderCreateTimeStart: orderCreateTimeEnd - 60 * 60 * 1000,
        orderCreateTimeEnd,
        statusEnumList: ['WAIT_PICKUP', 'WAIT_RIDER_ORDER', 'WAIT_RIDER_PICKUP', 'WAIT_ORDER', 'CANCELLED'],
      }) // 依次打印订单（待拣货/待骑手接单/待骑手取货）
      const printStatus = ['WAIT_PICKUP', 'WAIT_RIDER_ORDER', 'WAIT_RIDER_PICKUP']
      if (data.length > 0) {
        Vue.prototype.$GlobalEventBus.emit('refresh-takeaway-order')
        await Promise.allSettled([
          MT
            ? dispatch(
              'PROCESS_PRINT_ORDER',
              data.filter((i) => printStatus.includes(i.status))
            )
            : null,

          ifTakeVoice ? dispatch('PROCESS_VOICE_ALERT_ORDER', data) : null,
        ])
      }
    } catch (error) {
      console.error('轮询错误:', error)
    }

    commit('SET_PRINTING', false)
    const timer = setTimeout(() => dispatch('FETCH_PRINT_LIST'), state.pullInterval) // 递归轮询
    commit('SET_TIMER', timer)
  },

  /**
   * 打印进程
   * @param {*} param0
   * @param {*} orders
   */
  async PROCESS_PRINT_ORDER({ dispatch }, orders) {
    for (const order of orders) {
      await dispatch('PRINT_ORDER', order)
      await dispatch('UPDATE_ORDER', order)
    }
  },

  /**
   * 语音提醒进程
   * @param {*} param0
   * @param {*} orders 待接单、已取消
   */
  async PROCESS_VOICE_ALERT_ORDER({ dispatch }, orders) {
    // 待接单 首次提醒、3分钟提醒、5分钟提醒
    const subtleReminder = 3 * 60 * 1000

    const strongReminder = 5 * 60 * 1000

    for (const order of orders) {
      const interval = Date.now() - order.orderCreateTime
      const status = order.status
      if (status === 'WAIT_ORDER') {
        if (interval < subtleReminder) {
          Vue.prototype.$speek(i18n.t('您有新的外卖订单到了，请及时处理'), 'takeaway')
        } else if (interval < strongReminder) {
          Vue.prototype.$speek(i18n.t('订单即将超时，请尽快处理'), 'takeaway')
        } else {
          Vue.prototype.$speek(i18n.t('订单未接单，即将超时取消，请尽快处理'), 'takeaway')
          Vue.prototype.$mgAlert(i18n.t('订单未接单，即将超时取消，请尽快处理'), i18n.t('提示'), {
            showCancelButton: false,
          })
        }
      }

      if (status === 'CANCELLED') {
        Vue.prototype.$speek(i18n.t('订单已取消，请检查商品是否已发出'), 'takeaway')
        await dispatch('UPDATE_ORDER', order)
      }
    }
  },

  /**
   * 发起打印
   * @param {*} order
   */
  async PRINT_ORDER({ dispatch }, order) {
    const { PICKING_RECEIPT } = await dispatch('ENABLE_PRINT_PICKING')
    // 先打印拣货小票

    if (PICKING_RECEIPT) {
      await ipc.send(ipcApiRoute.print, {
        data: {
          ...order,
          memberName: encryptMemberName(order?.memberName || ''),
        },
        printType: PrintType.RECEIPT,
        businessType: BusinessType.WMJH,
      })

       // 等待拣货小票打印完成后再打印外卖订单小票，避免共享打印窗口竞态导致两次都打印成 WMDD
       await new Promise((resolve) => setTimeout(resolve, 1500))
    }

    await ipc.send(ipcApiRoute.print, {
      data: {
        ...order,
        memberName: encryptMemberName(order?.memberName || ''),
      },
      printType: PrintType.RECEIPT,
      businessType: BusinessType.WMDD,
    })
  },

  /**
   * 更新打印标识
   * @param {*} _
   * @param {*} order
   */
  async UPDATE_ORDER(_, order) {
    await updatePrint({ takeoutOrderId: order.takeoutOrderId })
  },

  async STOP({ state, commit }) {
    if (state.timer) {
      clearTimeout(state.timer)
      commit('SET_TIMER', null)
    }
    commit('SET_PRINTING', false)
  },

  /**
   * 外卖打印配置
   */
  async PRINT_SETTING(store) {
    const platformType = store.rootGetters['takeaway/platform']
    const { data } = await init(platformType)
    TakeawayPrintSetting.set({
      autoReceiveOrder: data.autoReceiveOrder,
      autoPrint: data.autoPrint,
      ifTakeVoice: data.ifTakeVoice,
    })
  },

  async ENABLE_AUTO_RECEIVE({ dispatch, rootGetters }, payload = null) {
    let visitorId = rootGetters['fingerprint/visitorId'] ?? payload?.visitorId
    // 美团自动接单配置
    // const autoReceiveDeviceMT = JSON.parse(TakeawayPrintSetting.get()?.autoReceiveOrder)?.MT
    const autoReceiveDeviceMT = TakeawayPrintSetting.get()?.autoReceiveOrder

    if (!visitorId) {
      const data = await dispatch('fingerprint/generateVisitorId', {}, { root: true })
      visitorId = data.visitorId
    }

    console.info(`设备指纹：${visitorId}`)

    return { MT: autoReceiveDeviceMT === visitorId }
  },

  async ENABLE_PRINT_PICKING({ dispatch, rootGetters }, payload = null) {
    let visitorId = rootGetters['fingerprint/visitorId'] ?? payload?.visitorId
    // 美团自动接单配置
    const printPicking = JSON.parse(TakeawayPrintSetting.get()?.autoPrint)?.PICKING_RECEIPT

    if (!visitorId) {
      const data = await dispatch('fingerprint/generateVisitorId', {}, { root: true })
      visitorId = data.visitorId
    }

    return { PICKING_RECEIPT: printPicking === visitorId }
  },
}
export default { state, mutations, actions }
